import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_PREFIX = "ori_survey_";

const VIEW = {
  LOADING: "loading",
  OPENING: "opening",
  TRANSITION: "transition",
  ITEMS: "items",
  TIER1: "tier1",
  TIER2: "tier2",
  TIER3: "tier3",
  REVIEW: "review",
  COMPLETE: "complete",
  ERROR: "error",
};

function wordCount(s) {
  if (!s) return 0;
  return s.trim().split(/\s+/).filter(Boolean).length;
}

export default function Survey() {
  const { token } = useParams();
  const [config, setConfig] = useState(null);
  const [view, setView] = useState(VIEW.LOADING);
  const [sectionIndex, setSectionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [textResponses, setTextResponses] = useState({});
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const startedAtRef = useRef(null);

  // Load + restore from localStorage
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/survey/${token}`);
        setConfig(data);
        if (data.already_completed) {
          setView(VIEW.COMPLETE);
        } else {
          const saved = localStorage.getItem(STORAGE_PREFIX + token);
          if (saved) {
            try {
              const parsed = JSON.parse(saved);
              setResponses(parsed.responses || {});
              setTextResponses(parsed.textResponses || {});
              setSectionIndex(parsed.sectionIndex || 0);
              setView(parsed.view || VIEW.OPENING);
            } catch {
              setView(VIEW.OPENING);
            }
          } else {
            setView(VIEW.OPENING);
          }
          startedAtRef.current = Date.now();
        }
      } catch (e) {
        setError(e?.response?.data?.detail || "We couldn't load this assessment. The link may have expired.");
        setView(VIEW.ERROR);
      }
    })();
  }, [token]);

  // Persist progress
  useEffect(() => {
    if (!config || view === VIEW.LOADING || view === VIEW.COMPLETE || view === VIEW.ERROR) return;
    localStorage.setItem(
      STORAGE_PREFIX + token,
      JSON.stringify({ responses, textResponses, sectionIndex, view }),
    );
  }, [responses, textResponses, sectionIndex, view, token, config]);

  const currentSection = config?.sections?.[sectionIndex] ?? null;
  const sectionItemNumbers = useMemo(
    () => (currentSection ? currentSection.items.map((i) => i.number) : []),
    [currentSection],
  );
  const sectionComplete = sectionItemNumbers.every((n) => responses[n] != null);
  const allComplete = useMemo(
    () => config && Object.keys(responses).length === config.total_items,
    [responses, config],
  );

  const tier1Prompts = useMemo(() => (config?.prompts || []).filter((p) => p.tier === 1), [config]);
  const tier2Prompts = useMemo(() => (config?.prompts || []).filter((p) => p.tier === 2), [config]);
  const tier3Prompts = useMemo(() => (config?.prompts || []).filter((p) => p.tier === 3), [config]);
  // Sprint 11 Phase 3c — derive the "required reflections complete"
  // gate from each prompt's own `required` flag, not from a hardcoded
  // tier-1 assumption. ORI has 4 required (T1–T4); PRI has 0.
  const requiredPrompts = useMemo(
    () => (config?.prompts || []).filter((p) => p.required),
    [config],
  );
  const requiredPromptsComplete = useMemo(
    () => requiredPrompts.every((p) => (textResponses[p.number] || "").trim().length > 0),
    [requiredPrompts, textResponses],
  );

  const selectResponse = useCallback((itemNumber, value) => {
    setResponses((prev) => ({ ...prev, [itemNumber]: value }));
  }, []);

  const setText = useCallback((promptNumber, text) => {
    setTextResponses((prev) => ({ ...prev, [promptNumber]: text }));
  }, []);

  async function handleSubmit() {
    if (!allComplete || !requiredPromptsComplete) return;
    setSubmitting(true);
    setError("");
    try {
      const text_responses = Object.entries(textResponses)
        .map(([n, t]) => ({ prompt_number: Number(n), text: (t || "").trim() }))
        .filter((r) => r.text.length > 0);
      const payload = {
        responses: Object.entries(responses).map(([n, v]) => ({
          item_number: Number(n),
          response_value: v,
        })),
        text_responses,
        duration_seconds: Math.round((Date.now() - (startedAtRef.current ?? Date.now())) / 1000),
      };
      await axios.post(`${API}/api/survey/${token}/submit`, payload);
      localStorage.removeItem(STORAGE_PREFIX + token);
      const { data } = await axios.get(`${API}/api/survey/${token}`);
      setConfig(data);
      setView(VIEW.COMPLETE);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409) {
        localStorage.removeItem(STORAGE_PREFIX + token);
        setView(VIEW.COMPLETE);
      } else {
        const msg =
          typeof detail === "string"
            ? detail
            : detail?.error === "required_text_missing"
              ? "Tier 1 is required — please answer T1 through T4 before submitting."
              : detail?.error || "Submission failed. Please try again.";
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (view === VIEW.LOADING) return <Loading />;
  if (view === VIEW.ERROR) return <ErrorScreen message={error} />;
  if (!config) return null;

  const scrollUp = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <div className="min-h-screen bg-paper" data-testid="survey-root">
      <SurveyHeader org={config.organization} />
      <main id="main-content" className="max-w-narrow mx-auto px-6 md:px-8 py-8 md:py-16">
        {view === VIEW.OPENING && (
          <Opening
            opening={config.opening}
            deploymentMode={config.deployment.deployment_mode || "organization"}
            onStart={() => {
              setView(VIEW.TRANSITION);
              setSectionIndex(0);
            }}
          />
        )}

        {view === VIEW.TRANSITION && currentSection && (
          <Transition
            section={currentSection}
            sectionIndex={sectionIndex}
            totalSections={config.sections.length}
            onContinue={() => setView(VIEW.ITEMS)}
          />
        )}

        {view === VIEW.ITEMS && currentSection && (
          <ItemsBlock
            section={currentSection}
            sectionIndex={sectionIndex}
            totalSections={config.sections.length}
            responses={responses}
            likertOptions={config.likert_options}
            onSelect={selectResponse}
            canAdvance={sectionComplete}
            isLast={sectionIndex === config.sections.length - 1}
            onAdvance={() => {
              if (sectionIndex < config.sections.length - 1) {
                setSectionIndex(sectionIndex + 1);
                setView(VIEW.TRANSITION);
                scrollUp();
              } else {
                setView(VIEW.TIER1);
                scrollUp();
              }
            }}
            onBack={() => {
              if (sectionIndex > 0) {
                setSectionIndex(sectionIndex - 1);
                setView(VIEW.ITEMS);
                scrollUp();
              } else {
                setView(VIEW.OPENING);
                scrollUp();
              }
            }}
          />
        )}

        {view === VIEW.TIER1 && (
          <TextTier
            tier={1}
            tierIntro={config.tier_intros["1"]}
            prompts={tier1Prompts}
            wordLimit={config.word_limit_soft}
            values={textResponses}
            onChange={setText}
            // Only gate the Tier 1 continue button when one or more
            // tier-1 prompts is `required` (ORI). PRI's tier-1 prompts
            // are all optional → no gate.
            requiredGate={tier1Prompts.some((p) => p.required) && !requiredPromptsComplete}
            requiredGateMessage="A short answer to each of the four is required. Empty is not the same as 'no answer.'"
            primaryLabel="Continue"
            onPrimary={() => {
              setView(VIEW.TIER2);
              scrollUp();
            }}
            onBack={() => {
              setSectionIndex(config.sections.length - 1);
              setView(VIEW.ITEMS);
              scrollUp();
            }}
          />
        )}

        {view === VIEW.TIER2 && (
          <TextTier
            tier={2}
            tierIntro={config.tier_intros["2"]}
            prompts={tier2Prompts}
            wordLimit={config.word_limit_soft}
            values={textResponses}
            onChange={setText}
            primaryLabel="Continue"
            secondaryLabel="Skip this tier"
            onPrimary={() => {
              setView(VIEW.TIER3);
              scrollUp();
            }}
            onSecondary={() => {
              setView(VIEW.TIER3);
              scrollUp();
            }}
            onBack={() => {
              setView(VIEW.TIER1);
              scrollUp();
            }}
          />
        )}

        {view === VIEW.TIER3 && (
          <TextTier
            tier={3}
            tierIntro={config.tier_intros["3"]}
            prompts={tier3Prompts}
            wordLimit={config.word_limit_soft}
            values={textResponses}
            onChange={setText}
            primaryLabel="Review"
            secondaryLabel="Skip and review"
            onPrimary={() => {
              setView(VIEW.REVIEW);
              scrollUp();
            }}
            onSecondary={() => {
              setView(VIEW.REVIEW);
              scrollUp();
            }}
            onBack={() => {
              setView(VIEW.TIER2);
              scrollUp();
            }}
          />
        )}

        {view === VIEW.REVIEW && (
          <Review
            allComplete={allComplete && requiredPromptsComplete}
            sections={config.sections}
            responses={responses}
            textResponses={textResponses}
            tier1Prompts={tier1Prompts}
            tier2Prompts={tier2Prompts}
            tier3Prompts={tier3Prompts}
            deploymentMode={config.deployment.deployment_mode || "organization"}
            onEdit={(idx) => {
              setSectionIndex(idx);
              setView(VIEW.ITEMS);
            }}
            onEditTier={(t) =>
              setView(t === 1 ? VIEW.TIER1 : t === 2 ? VIEW.TIER2 : VIEW.TIER3)
            }
            onSubmit={handleSubmit}
            submitting={submitting}
            error={error}
          />
        )}

        {view === VIEW.COMPLETE && (
          <Complete
            completion={config.completion_copy}
            individualReportsEnabled={config.deployment.individual_reports_enabled}
            token={token}
          />
        )}
      </main>
    </div>
  );
}

function SurveyHeader({ org }) {
  return (
    <header className="border-b border-gray-100 bg-paper" data-testid="survey-header">
      <div className="max-w-narrow mx-auto px-6 md:px-8 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="wordmark text-xs">
            <span style={{ color: "#1A1A1A" }}>RESILIENT</span>{" "}
            <span style={{ color: "#2F6B7C" }}>TEAMS</span>
          </span>
        </div>
        <div className="text-xs text-gray-500" data-testid="survey-org">
          {org?.name}
        </div>
      </div>
    </header>
  );
}

function ProgressDots({ current, total }) {
  return (
    <div
      className="flex gap-2 items-center"
      data-testid="progress-dots"
      aria-label={`Section ${current + 1} of ${total}`}
    >
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className="h-1 flex-1 transition-all duration-300"
          style={{
            background: i < current ? "#AD393E" : i === current ? "#4592A8" : "#EEEEEE",
            maxWidth: "32px",
          }}
        />
      ))}
    </div>
  );
}

function Opening({ opening, onStart, deploymentMode }) {
  // Mode-aware framing for the survey landing.
  // ORI (organization/team): "A team reflection" / "Let's Understand Team Resilience"
  // PRI (individual): "A personal reflection" / "Let's Understand Your Resilience"
  // The three body paragraphs (welcome / time_structure / confidentiality)
  // are already mode-aware server-side (backend/microcopy.py's OPENING vs
  // INDIVIDUAL_OPENING); only the eyebrow + H1 needed fixing.
  const isIndividual = deploymentMode === "individual";
  const eyebrow = isIndividual ? "A personal reflection" : "A team reflection";
  const headline = isIndividual ? "Your Resilience" : "Team Resilience";
  return (
    <div className="fade-up" data-testid="opening-screen">
      <div className="eyebrow mb-6" data-testid="opening-eyebrow">{eyebrow}</div>
      <h1 style={{ fontSize: "clamp(2.5rem, 6vw, 4.25rem)" }} className="mb-10">
        Let&rsquo;s Understand
        <br />
        {headline}
      </h1>
      <div className="space-y-6 mb-12 max-w-prose">
        <p className="lead text-xl md:text-2xl" data-testid="opening-welcome">
          {opening.welcome}
        </p>
        <p className="text-ink leading-relaxed" data-testid="opening-time">
          {opening.time_structure}
        </p>
        <p className="text-gray-700 leading-relaxed" data-testid="opening-confidentiality">
          {opening.confidentiality}
        </p>
      </div>
      <button onClick={onStart} className="btn-primary" data-testid="opening-begin-button">
        Begin the reflection
      </button>
    </div>
  );
}

function Transition({ section, sectionIndex, totalSections, onContinue }) {
  return (
    <div className="fade-up" data-testid={`transition-${section.dimension}`}>
      <div className="mb-12">
        <ProgressDots current={sectionIndex} total={totalSections} />
      </div>
      <div className="eyebrow mb-6" data-testid="transition-label">
        {section.transition_label}
      </div>
      <h2 style={{ fontSize: "clamp(2rem, 4.5vw, 3.25rem)" }} className="mb-10">
        {section.dimension_label}
      </h2>
      <div className="space-y-8 mb-14 max-w-prose">
        <p className="lead text-xl md:text-2xl" data-testid="transition-framing">
          {section.transition_framing}
        </p>
        <div className="pt-6 border-t border-gray-100">
          <div className="eyebrow mb-3" style={{ color: "#AD393E" }}>
            Hold in mind
          </div>
          <p className="lead text-lg md:text-xl" data-testid="transition-hold-in-mind">
            {section.hold_in_mind}
          </p>
        </div>
      </div>
      <button onClick={onContinue} className="btn-primary" data-testid="transition-continue-button">
        Continue
      </button>
    </div>
  );
}

function ItemsBlock({
  section,
  sectionIndex,
  totalSections,
  responses,
  likertOptions,
  onSelect,
  canAdvance,
  isLast,
  onAdvance,
  onBack,
}) {
  return (
    <div className="fade-up" data-testid={`items-${section.dimension}`}>
      <div className="mb-10">
        <ProgressDots current={sectionIndex} total={totalSections} />
      </div>
      <div className="flex items-baseline justify-between mb-10">
        <div>
          <div className="eyebrow mb-2">{section.transition_label}</div>
          <h2 style={{ fontSize: "clamp(1.5rem, 3vw, 2rem)" }}>{section.dimension_label}</h2>
        </div>
      </div>

      <ol className="space-y-10 mb-12">
        {section.items.map((item, idx) => (
          <li
            key={item.number}
            data-testid={`item-row-${item.number}`}
            className="border-b border-gray-100 pb-8 last:border-0"
          >
            <div className="flex items-start gap-4 mb-6">
              <span
                className="text-xs text-gray-500 mt-1"
                style={{ fontFamily: "Oswald", letterSpacing: "0.1em" }}
              >
                {String(idx + 1).padStart(2, "0")}
              </span>
              <p className="text-lg md:text-xl text-ink leading-snug max-w-prose">{item.text}</p>
            </div>
            <LikertRow
              itemNumber={item.number}
              options={likertOptions}
              selected={responses[item.number]}
              onSelect={(v) => onSelect(item.number, v)}
            />
          </li>
        ))}
      </ol>

      {!canAdvance && (
        <div className="mb-8 text-sm text-gray-700" data-testid="items-incomplete-notice">
          A few items still to answer in this section.
        </div>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <button onClick={onBack} className="btn-quiet" data-testid="items-back-button">
          ← Back
        </button>
        <button
          onClick={onAdvance}
          disabled={!canAdvance}
          className="btn-primary"
          data-testid="items-advance-button"
        >
          {isLast ? "Continue to reflection" : "Continue"}
        </button>
      </div>
    </div>
  );
}

function LikertRow({ itemNumber, options, selected, onSelect }) {
  return (
    <div
      role="radiogroup"
      aria-label={`Response for item ${itemNumber}`}
      className="grid grid-cols-5 gap-1 md:gap-3"
      data-testid={`likert-row-${itemNumber}`}
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          role="radio"
          aria-checked={selected === opt.value}
          onClick={() => onSelect(opt.value)}
          className={`likert-option ${selected === opt.value ? "selected" : ""}`}
          data-testid={`likert-${itemNumber}-${opt.value}`}
        >
          <div className="dot" />
          <div className="text-label">{opt.label}</div>
        </button>
      ))}
    </div>
  );
}

function TextTier({
  tier,
  tierIntro,
  prompts,
  wordLimit,
  values,
  onChange,
  requiredGate,
  requiredGateMessage,
  primaryLabel,
  secondaryLabel,
  onPrimary,
  onSecondary,
  onBack,
}) {
  return (
    <div className="fade-up" data-testid={`tier-${tier}`}>
      <div className="eyebrow mb-3" data-testid={`tier-${tier}-label`}>
        {tierIntro.label}
      </div>
      <h2 style={{ fontSize: "clamp(2rem, 4vw, 2.75rem)" }} className="mb-6">
        A reflection in your own words.
      </h2>
      <p className="lead text-lg md:text-xl max-w-prose mb-12" data-testid={`tier-${tier}-framing`}>
        {tierIntro.framing}
      </p>

      <div className="space-y-12 mb-12">
        {prompts.map((p) => (
          <PromptBlock
            key={p.number}
            prompt={p}
            value={values[p.number] || ""}
            wordLimit={wordLimit}
            onChange={(t) => onChange(p.number, t)}
          />
        ))}
      </div>

      {requiredGate && (
        <div className="mb-6 text-sm text-gray-700" data-testid={`tier-${tier}-required-notice`}>
          {requiredGateMessage}
        </div>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-gray-100 gap-4 flex-wrap">
        <button onClick={onBack} className="btn-quiet" data-testid={`tier-${tier}-back-button`}>
          ← Back
        </button>
        <div className="flex items-center gap-4">
          {secondaryLabel && (
            <button
              onClick={onSecondary}
              className="btn-quiet"
              data-testid={`tier-${tier}-skip-button`}
            >
              {secondaryLabel}
            </button>
          )}
          <button
            onClick={onPrimary}
            disabled={requiredGate}
            className="btn-primary"
            data-testid={`tier-${tier}-continue-button`}
          >
            {primaryLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function PromptBlock({ prompt, value, wordLimit, onChange }) {
  const wc = wordCount(value);
  const over = wc > wordLimit;
  return (
    <div data-testid={`prompt-${prompt.number}`}>
      <div className="flex items-baseline justify-between mb-3">
        <div className="eyebrow" style={{ color: prompt.required ? "#AD393E" : "#4592A8" }}>
          {prompt.code} {prompt.required ? "· Required" : "· Optional"}
        </div>
      </div>
      <p
        className="text-lg md:text-xl text-ink max-w-prose mb-4"
        style={{ fontFamily: "Inter", lineHeight: 1.4 }}
      >
        {prompt.prompt}
      </p>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
        className="field"
        style={{
          fontFamily: "Inter",
          lineHeight: 1.55,
          resize: "vertical",
          minHeight: "8rem",
        }}
        placeholder={
          prompt.required ? "A few sentences." : "Skip if there's no real answer for you."
        }
        data-testid={`prompt-${prompt.number}-input`}
      />
      <div className="flex items-center justify-between mt-2">
        <div className="text-xs text-gray-500">
          {prompt.required && wc === 0 ? "Required" : ""}
        </div>
        <div
          className="text-xs"
          style={{
            fontFamily: "Oswald",
            letterSpacing: "0.1em",
            color: over ? "#AD393E" : "#6F6F6F",
          }}
          data-testid={`prompt-${prompt.number}-counter`}
        >
          {wc} / {wordLimit} words{" "}
          {over ? "· that's long — short answers serve facilitators better, but no hard limit" : ""}
        </div>
      </div>
    </div>
  );
}

function Review({
  allComplete,
  sections,
  responses,
  textResponses,
  tier1Prompts,
  tier2Prompts,
  tier3Prompts,
  deploymentMode,
  onEdit,
  onEditTier,
  onSubmit,
  submitting,
  error,
}) {
  const answeredCount = Object.keys(responses).length;
  const tier1Filled = tier1Prompts.filter((p) => (textResponses[p.number] || "").trim()).length;
  const tier2Filled = tier2Prompts.filter((p) => (textResponses[p.number] || "").trim()).length;
  const tier3Filled = tier3Prompts.filter((p) => (textResponses[p.number] || "").trim()).length;
  const totalText = tier1Filled + tier2Filled + tier3Filled;
  return (
    <div className="fade-up" data-testid="review-screen">
      <div className="eyebrow mb-6">Last step</div>
      <h2 style={{ fontSize: "clamp(2rem, 4.5vw, 3.25rem)" }} className="mb-6">
        A quick review.
      </h2>
      <p className="lead text-lg md:text-xl max-w-prose mb-10">
        {answeredCount} of 56 items answered. {totalText} reflection{totalText === 1 ? "" : "s"}{" "}
        written. You can revisit any section before you submit.
      </p>

      <div className="space-y-3 mb-10">
        {sections.map((s, i) => {
          const sectionAnswered = s.items.filter((it) => responses[it.number] != null).length;
          const full = sectionAnswered === s.items.length;
          return (
            <button
              key={s.dimension}
              onClick={() => onEdit(i)}
              className="w-full flex items-center justify-between py-4 px-5 border bg-paper hover:border-teal transition-colors text-left"
              style={{ borderColor: full ? "#EEEEEE" : "#AD393E" }}
              data-testid={`review-section-${s.dimension}`}
            >
              <div>
                <div
                  className="eyebrow mb-1"
                  style={{ color: full ? "#4592A8" : "#AD393E" }}
                >
                  {s.transition_label}
                </div>
                <div
                  className="text-base text-ink"
                  style={{ fontFamily: "Inter", fontWeight: 500 }}
                >
                  {s.dimension_label}
                </div>
              </div>
              <div
                className="text-sm"
                style={{
                  color: full ? "#6F6F6F" : "#AD393E",
                  fontFamily: "Oswald",
                  letterSpacing: "0.1em",
                }}
              >
                {sectionAnswered}/{s.items.length} {full ? "✓" : ""}
              </div>
            </button>
          );
        })}

        <ReviewTierRow
          testId="review-tier-1"
          label="Reflection · Tier 1 — Required"
          subtitle="Four short questions about your team."
          filled={tier1Filled}
          total={tier1Prompts.length}
          required={true}
          onEdit={() => onEditTier(1)}
        />
        <ReviewTierRow
          testId="review-tier-2"
          label="Reflection · Tier 2 — Optional"
          subtitle="One per dimension. Skip any without a real answer."
          filled={tier2Filled}
          total={tier2Prompts.length}
          required={false}
          onEdit={() => onEditTier(2)}
        />
        <ReviewTierRow
          testId="review-tier-3"
          label="Reflection · Tier 3 — Optional"
          subtitle="Two final notes for your facilitator."
          filled={tier3Filled}
          total={tier3Prompts.length}
          required={false}
          onEdit={() => onEditTier(3)}
        />
      </div>

      {error && (
        <div
          className="mb-6 py-3 px-4 text-sm"
          style={{ color: "#AD393E", background: "#FBFAF7", border: "1px solid #AD393E" }}
          data-testid="review-error"
        >
          {error}
        </div>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-gray-100 gap-4 flex-wrap">
        <div className="text-sm text-gray-500 max-w-prose">
          {deploymentMode === "individual" ? (
            <>
              Your responses go directly to your coach. Free-text reflections are read by your
              coach only — never scored, never auto-summarized.
            </>
          ) : (
            <>
              Your responses are not shared with your manager. Free-text reflections are read by your
              facilitator only — never scored, never auto-summarized.
            </>
          )}
        </div>
        <button
          onClick={onSubmit}
          disabled={!allComplete || submitting}
          className="btn-primary shrink-0"
          data-testid="review-submit-button"
        >
          {submitting ? "Saving…" : "Submit reflection"}
        </button>
      </div>
    </div>
  );
}

function ReviewTierRow({ testId, label, subtitle, filled, total, required, onEdit }) {
  const incomplete = required && filled < total;
  return (
    <button
      onClick={onEdit}
      className="w-full flex items-center justify-between py-4 px-5 border bg-paper hover:border-teal transition-colors text-left"
      style={{ borderColor: incomplete ? "#AD393E" : "#EEEEEE" }}
      data-testid={testId}
    >
      <div>
        <div
          className="eyebrow mb-1"
          style={{ color: incomplete ? "#AD393E" : "#4592A8" }}
        >
          {label}
        </div>
        <div className="text-sm text-gray-700">{subtitle}</div>
      </div>
      <div
        className="text-sm"
        style={{
          color: incomplete ? "#AD393E" : "#6F6F6F",
          fontFamily: "Oswald",
          letterSpacing: "0.1em",
        }}
      >
        {filled}/{total} {!incomplete && filled > 0 ? "✓" : ""}
      </div>
    </button>
  );
}

function Complete({ completion, individualReportsEnabled, token }) {
  return (
    <div className="fade-up py-12" data-testid="completion-screen">
      <div className="eyebrow mb-8" style={{ color: "#2F6B7C" }}>
        Complete
      </div>
      <h2
        style={{ fontSize: "clamp(2.5rem, 5vw, 3.75rem)" }}
        className="mb-10"
        data-testid="completion-main"
      >
        {completion.main}
      </h2>
      <p className="lead text-xl md:text-2xl max-w-prose" data-testid="completion-next">
        {individualReportsEnabled
          ? completion.individual_reports_enabled
          : completion.individual_reports_disabled}
      </p>
      {individualReportsEnabled && (
        <div className="mt-10">
          <a
            href={`/respondent-report/${token}`}
            data-testid="view-your-reflection-cta"
            className="btn-primary inline-block"
            style={{
              fontFamily: "Oswald",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              fontSize: "13px",
              padding: "14px 28px",
              background: "#1A1A1A",
              color: "#FBFAF7",
              textDecoration: "none",
              border: 0,
            }}
          >
            Read your reflection
          </a>
        </div>
      )}
      <div className="mt-16 pt-6 border-t border-gray-100 text-xs text-gray-500 max-w-prose">
        You can close this window now.
      </div>
    </div>
  );
}

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-paper">
      <div className="eyebrow text-gray-500">Loading…</div>
    </div>
  );
}

function ErrorScreen({ message }) {
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-paper p-8"
      data-testid="survey-error"
    >
      <div className="max-w-md text-center">
        <div className="eyebrow mb-6" style={{ color: "#AD393E" }}>
          This link can&rsquo;t be opened
        </div>
        <p className="lead text-lg mb-2">
          {message || "The link may have expired or already been used."}
        </p>
        <p className="text-sm text-gray-500 mt-6">
          If you believe this is a mistake, reach out to your facilitator.
        </p>
      </div>
    </div>
  );
}
